import {Routes} from '@angular/router';
import {editorLeaveGuard} from '../shared/layouts/editor-layout/editor-leave.guard';
import {adminGuard, authGuard} from '../auth/auth.guard';
import {AuthLayoutComponent} from '../layouts/auth-layout/auth-layout.component';
import {ShellLayoutComponent} from '../layouts/shell-layout/shell-layout.component';
import {SsrDemoComponent} from '../ssr-demo/ssr-demo.component';
import {localizedLanguageRouteCanMatch} from './route-matchers';
import {authChildRoutes} from './auth.routes';
import {localizedPublicLanguageGuard} from './localized-public-language.guard';
import {apiCompatibilityGuard} from '../shared/api/api-compatibility.guard';

const shellChildRoutes: Routes = [
  {path: 'search', data: {screenKey: 'FullSearch'}, loadComponent: () => import('../search/search.page').then(m => m.SearchPage)},
  {
    path: 'basket', children: [
      {

        data: {screenKey: 'BasketJournalDetail'}, path: 'journal/:id', loadComponent: () => import('../basket/basket-journal-detail.page').then(m => m.BasketJournalDetailPage)},
      {
        data: {screenKey: 'BasketJournal'}, path: 'journal', loadComponent: () => import('../basket/basket-journal.page').then(m => m.BasketJournalPage)},
      {
        data: {screenKey: 'BasketPurchase'}, path: ':id/acquire', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../basket/basket-purchase.page').then(m => m.BasketPurchasePage)},
      {
        data: {screenKey: 'BasketNeedAccess'}, path: ':id/access', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../basket/basket-need-access.page').then(m => m.BasketNeedAccessPage)},
      {
        data: {screenKey: 'ShopEditor.New'}, path: 'shops/new', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../settings/shops/shop-editor.page').then(m => m.ShopEditorPage)},
      {
        data: {screenKey: 'ShopEditor.Edit'}, path: 'shops/:id/edit', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../settings/shops/shop-editor.page').then(m => m.ShopEditorPage)},
      {
        data: {screenKey: 'Shops'}, path: 'shops', loadComponent: () => import('../settings/shops/shops.page').then(m => m.ShopsPage)},
      {
        data: {screenKey: 'BasketEditor.New'}, path: 'new', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../basket/basket-editor.page').then(m => m.BasketEditorPage)},
      {
        data: {screenKey: 'BasketNeedHistory'}, path: ':id/history', loadComponent: () => import('../basket/basket-need-history.page').then(m => m.BasketNeedHistoryPage)},
      {
        data: {screenKey: 'BasketEditor.Edit'}, path: ':id/edit', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../basket/basket-editor.page').then(m => m.BasketEditorPage)},
      {
        data: {screenKey: 'Basket'}, path: '', pathMatch: 'full', loadComponent: () => import('../basket/basket.page').then(m => m.BasketPage)},
    ],
  },
  {

    data: {screenKey: 'Home'},
    path: 'home',
    loadChildren: () => import('../home/home.module').then(m => m.HomePageModule),
  },
  {
    path: 'inventory',
    children: [
      {

        data: {screenKey: 'Favorites'},
        path: 'favorites',
        loadComponent: () => import('../favorites/favorites.page').then(m => m.FavoritesPage),
      },
      {

        data: {screenKey: 'Containers'},
        path: 'containers',
        loadChildren: () => import('../containers/containers.module').then(m => m.ContainersPageModule),
      },
    ],
  },
  {

    data: {screenKey: 'QrLabels'},
    path: 'qr-labels',
    loadChildren: () => import('../qr-labels/qr-labels.module').then(m => m.QrLabelsPageModule),
  },
  {

    data: {screenKey: 'WriteOff'},
    path: 'write-off',
    loadChildren: () => import('../write-off/write-off.module').then(m => m.WriteOffPageModule),
  },
  {

    data: {screenKey: 'Relocation'},
    path: 'relocation',
    loadChildren: () => import('../relocation/relocation.module').then(m => m.RelocationPageModule),
  },
  {

    data: {screenKey: 'Terms'},
    path: 'terms',
    loadChildren: () => import('../terms/terms.module').then(m => m.TermsPageModule),
  },
  {

    data: {screenKey: 'Privacy'},
    path: 'privacy',
    loadChildren: () => import('../privacy/privacy.module').then(m => m.PrivacyPageModule),
  },
  {
    path: 'reports',
    pathMatch: 'full',
    redirectTo: 'reports/expiring',
  },
  {

    data: {screenKey: 'LastChanges'},
    path: 'reports/last-changes',
    loadChildren: () => import('../reports/last-changes/last-changes.module').then(m => m.LastChangesPageModule),
  },
  {

    data: {screenKeyFallback: 'Reports.Expiring', screenKeyParam: 'reportKind', screenKeys: {expiring: 'Reports.Expiring', expired: 'Reports.Expired'}},
    path: 'reports/:reportKind',
    loadChildren: () => import('../reports/reports.module').then(m => m.ReportsPageModule),
  },
  {

    data: {screenKey: 'People'},
    path: 'people',
    loadChildren: () => import('../people/people.module').then(m => m.PeoplePageModule),
  },
  {

    data: {screenKey: 'Feedback'},
    path: 'feedback',
    canActivate: [authGuard],
    loadChildren: () => import('../feedback/feedback.module').then(m => m.FeedbackPageModule),
  },
  {

    data: {screenKey: 'Moderation'},
    path: 'moderation',
    loadChildren: () => import('../moderation/moderation.module').then(m => m.ModerationModule),
  },
  {
    path: 'administration',
    canActivate: [adminGuard],
    children: [
      {path: 'releases/:id', canActivate: [adminGuard], canDeactivate: [editorLeaveGuard], data: {screenKey: 'AdminReleases.Detail'}, loadComponent: () => import('../administration/releases/administration-release-detail.page').then(m => m.AdministrationReleaseDetailPage)},
      {path: 'releases', canActivate: [adminGuard], data: {screenKey: 'AdminReleases.List'}, loadComponent: () => import('../administration/releases/administration-releases.page').then(m => m.AdministrationReleasesPage)},
      {path: 'appearance/:id/edit', canActivate: [adminGuard], data: {screenKey: 'AdminAppearance.Edit', standardAdmin: true}, canDeactivate: [editorLeaveGuard], loadComponent: () => import('../appearance/appearance.page').then(m => m.AppearancePage)},
      {path: 'appearance', canActivate: [adminGuard], data: {screenKey: 'AdminAppearance.List', standardAdmin: true}, loadComponent: () => import('../appearance/appearance.page').then(m => m.AppearancePage)},
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'users',
      },
      {

        data: {screenKey: 'AdministrationUsers'},
        path: 'users',
        loadChildren: () => import('../administration/users/administration-users.module').then(m => m.AdministrationUsersPageModule),
      },
      {

        data: {screenKey: 'AdministrationFeedback'},
        path: 'feedback',
        loadChildren: () => import('../administration/feedback/administration-feedback.module').then(m => m.AdministrationFeedbackPageModule),
      },
      {

        data: {screenKey: 'AdministrationNotifications'},
        path: 'notifications/push', pathMatch: 'full', redirectTo: 'operational-settings/notifications',
      },
      {
        path: 'notifications', pathMatch: 'full', redirectTo: 'operational-settings/notifications',
      },
      {

        data: {screenKey: 'Audits'},
        path: 'audits',
        loadChildren: () => import('../administration/audits/audits.module').then(m => m.AuditsModule),
      },
      {

        data: {screenKey: 'OperationalSettings'},
        path: 'operational-settings',
        loadChildren: () => import('../administration/operational-settings/operational-settings.module').then(m => m.OperationalSettingsModule),
      },
      {

        data: {screenKey: 'PaymentProviders'},
        path: 'payment-providers',
        loadChildren: () => import('../administration/payment-providers/payment-providers.module').then(m => m.PaymentProvidersModule),
      },
      {

        data: {screenKey: 'PricingPlans'},
        path: 'pricing-plans',
        loadChildren: () => import('../administration/pricing-plans/pricing-plans.module').then(m => m.PricingPlansModule),
      },
    ],
  },
  {
    path: 'profile',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'onboarding',
      },
      {

        data: {screenKey: 'Onboarding'},
        path: 'onboarding',
        loadChildren: () => import('../onboarding/onboarding.module').then(m => m.OnboardingPageModule),
      },
      {
        path: 'mailings', data: {screenKey: 'Mailings'},
        loadComponent: () => import('../mailings/mailings.page').then(m => m.MailingsPage),
      },
      {

        data: {screenKey: 'Settings'},
        path: 'settings',
        loadChildren: () => import('../settings/settings.module').then(m => m.SettingsPageModule),
      },
      {

        data: {screenKey: 'Appearance.Edit'}, path: 'appearance/:id/edit', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../appearance/appearance.page').then(m => m.AppearancePage)},
      {path: 'appearance/:id/clone', data: {screenKey: 'Appearance.Clone', clone: true}, canDeactivate: [editorLeaveGuard], loadComponent: () => import('../appearance/appearance.page').then(m => m.AppearancePage)},
      {

        data: {screenKey: 'Appearance.List'}, path: 'appearance', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../appearance/appearance.page').then(m => m.AppearancePage)},
      {
        path: 'shops',
        children: [
          {

            data: {screenKey: 'ShopEditor.New'}, path: 'new', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../settings/shops/shop-editor.page').then(m => m.ShopEditorPage)},
          {
            data: {screenKey: 'ShopEditor.Edit'}, path: ':id/edit', canDeactivate: [editorLeaveGuard], loadComponent: () => import('../settings/shops/shop-editor.page').then(m => m.ShopEditorPage)},
          {
            data: {screenKey: 'Shops'}, path: '', pathMatch: 'full', loadComponent: () => import('../settings/shops/shops.page').then(m => m.ShopsPage)},
        ],
      },
      {

        data: {screenKey: 'SubscriptionSummary'},
        path: 'subscription',
        loadComponent: () => import('../settings/subscription-summary/subscription-summary.component').then(m => m.SubscriptionSummaryComponent),
      },
    ],
  },
  {

    data: {screenKey: 'About'},
    path: 'about',
    loadChildren: () => import('../about/about.module').then(m => m.AboutPageModule),
  },
  {

    data: {screenKey: 'Terms'},
    path: 'terms',
    loadChildren: () => import('../terms/terms.module').then(m => m.TermsPageModule),
  },
  {

    data: {screenKey: 'Privacy'},
    path: 'privacy',
    loadChildren: () => import('../privacy/privacy.module').then(m => m.PrivacyPageModule),
  },
];

export const routes: Routes = [
  {path: 'whats-new', data: {screenKey: 'WhatsNew'}, loadComponent: () => import('../whats-new/whats-new.page').then(m => m.WhatsNewPage)},
  {

    data: {screenKey: 'Checkout'},
    path: 'checkout',
    loadChildren: () => import('../checkout/checkout.module').then(m => m.CheckoutPageModule),
  },
  {

    data: {screenKey: 'About'},
    path: 'about',
    loadChildren: () => import('../about/about.module').then(m => m.AboutPageModule),
  },
  {

    data: {screenKey: 'Terms'},
    path: 'terms',
    loadChildren: () => import('../terms/terms.module').then(m => m.TermsPageModule),
  },
  {

    data: {screenKey: 'Privacy'},
    path: 'privacy',
    loadChildren: () => import('../privacy/privacy.module').then(m => m.PrivacyPageModule),
  },
  {

    data: {screenKey: 'AccountDeletion'},
    path: 'account-deletion',
    loadChildren: () => import('../account-deletion/account-deletion.module').then(m => m.AccountDeletionPageModule),
  },
  {

    data: {screenKey: 'Landing'},
    path: '',
    pathMatch: 'full',
    loadChildren: () => import('../landing/landing.module').then(m => m.LandingPageModule),
  },
  {

    data: {screenKey: 'PublicItems'},
    path: 'items',
    loadChildren: () => import('../public-items/public-items.module').then(m => m.PublicItemsPageModule),
  },
  {

    data: {screenKey: 'PublicItemDetail'},
    path: 'items/:publicId/:slug',
    loadChildren: () => import('../public-items/public-item-detail.module').then(m => m.PublicItemDetailPageModule),
  },
  {

    data: {screenKey: 'SsrDemo'},
    path: 'ssr-demo',
    component: SsrDemoComponent,
  },
  {
    path: ':language',
    canMatch: [localizedLanguageRouteCanMatch],
    children: [
      {path: 'whats-new', canActivate: [localizedPublicLanguageGuard], data: {screenKey: 'WhatsNew'}, loadComponent: () => import('../whats-new/whats-new.page').then(m => m.WhatsNewPage)},
      {

        data: {screenKey: 'Checkout'},
        path: 'checkout',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../checkout/checkout.module').then(m => m.CheckoutPageModule),
      },
      {

        data: {screenKey: 'About'},
        path: 'about',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../about/about.module').then(m => m.AboutPageModule),
      },
      {

        data: {screenKey: 'Terms'},
        path: 'terms',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../terms/terms.module').then(m => m.TermsPageModule),
      },
      {

        data: {screenKey: 'Privacy'},
        path: 'privacy',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../privacy/privacy.module').then(m => m.PrivacyPageModule),
      },
      {

        data: {screenKey: 'AccountDeletion'},
        path: 'account-deletion',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../account-deletion/account-deletion.module').then(m => m.AccountDeletionPageModule),
      },
      {

        data: {screenKey: 'Guides'},
        path: 'guides',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../guides/guides.module').then(m => m.GuidesModule),
      },
      {

        data: {screenKey: 'Landing'},
        path: '',
        pathMatch: 'full',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../landing/landing.module').then(m => m.LandingPageModule),
      },
      {

        data: {screenKey: 'PublicItems'},
        path: 'items',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../public-items/public-items.module').then(m => m.PublicItemsPageModule),
      },
      {

        data: {screenKey: 'PublicItemDetail'},
        path: 'items/:publicId/:slug',
        canActivate: [localizedPublicLanguageGuard],
        loadChildren: () => import('../public-items/public-item-detail.module').then(m => m.PublicItemDetailPageModule),
      },
      {
        path: '',
        component: AuthLayoutComponent,
        children: [
          ...authChildRoutes,
        ],
      },
      {
        path: '',
        component: ShellLayoutComponent,
        canActivate: [apiCompatibilityGuard],
        children: [
          ...shellChildRoutes,
        ],
      },
    ],
  },
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      ...authChildRoutes,
    ],
  },
  {
    path: '',
    component: ShellLayoutComponent,
    canActivate: [apiCompatibilityGuard],
    children: [
      ...shellChildRoutes,
    ],
  },
  {
    path: '**',
    redirectTo: 'login',
  },
];
